import * as Yup from 'yup';
import { getIn } from 'formik';
import { lookupsJson, paymentMethod, FarmRiskType } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { capitalize } from 'core/helpers/formatters';
import { appConfig } from 'brand-config';

const primaryHeatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;

const states = lookupsJson.usStates;

const mortgageFieldsNameMap = {
  loanNumber: 'Loan number',
  mortgageHolderName: 'Mortgage lender name',
  'mortgageHolderAddress.address': 'Address',
  'mortgageHolderAddress.city': 'City',
  'mortgageHolderAddress.zip': 'Zip code',
  'mortgageHolderAddress.state': 'State'
};

export function validUSState(name) {
  return this.test({
    name: `${name}.validUSState`,
    message: 'Please enter a valid US state',
    test: (val) => !val || states.some((state) => state.id === val),
    exclusive: false
  });
}

export function addDriverNameToError(requiredFieldName) {
  return this.test(requiredFieldName, '', function (val) {
    const { index } = this.options;
    const { firstName, lastName } = this.options.context.drivers[index];
    if (!val?.length) {
      return this.createError({
        message: `${requiredFieldName} is required for ${capitalize(firstName)} ${capitalize(lastName)}`
      });
    }
    return true;
  });
}

export function validHomePurchaseDate(name, isRequired = true) {
  return this.test({
    name: `${name}.validHomePurchaseDate`,
    message: 'Home purchase date cannot be before year built',
    test: function test(val) {
      if (!val && isRequired) {
        return this.createError({
          message: 'Purchase date is required',
          path: name
        });
      }

      const propertyType = name.includes('home') ? 'home' : 'condo';
      const yearBuilt = this.options.context?.[propertyType]?.yearBuilt;
      const yearPurchasedInput = Number(this.options.context?.[propertyType]?.purchaseDate?.split('-')?.[0]);

      if (yearPurchasedInput && yearPurchasedInput < yearBuilt) {
        return this.createError({
          message: 'Purchase date cannot be before year built',
          path: name
        });
      }

      return true;
    }
  });
}

export function requiredString(label) {
  return this.ensure().required(label || 'Required');
}

const ADDRESS_SUBFIELDS = ['address', 'city', 'state', 'zip'];

const isAddressEmpty = (container) =>
  !container || ADDRESS_SUBFIELDS.every((field) => !container[field] || !String(container[field]).trim());

/**
 * Builds the five address subfield validators with collapsed messaging (#30230).
 *
 * The Validation Errors modal groups errors under a section header (e.g. "Lienholders"), so
 * the messages here intentionally do NOT repeat the address's label: an entirely-empty required
 * address surfaces a single "<label> is required" summary (via the block-level test in
 * `addressBlock`, or the container's own required rule), and once the block is in-progress the
 * subfield rows are terse ("Street is required", "City is required", …) so they don't echo the
 * section header above them. The `<label>` lives only on the empty-block summary, where the row
 * needs to stand on its own.
 *
 * `getAddressContainer(testCtx)` returns the object holding the address subfields, so the
 * "is the block empty?" check works for BOTH nested addresses (subfields live on a child
 * `address` object — container is `this.parent`) and flat addresses (subfields live
 * directly on the item — container is also `this.parent`). Both pass `(ctx) => ctx.parent`.
 *
 * @param {string} label - Human name of the address (e.g. "Lienholder address").
 * @param {object} [opts]
 * @param {Yup.Schema} [opts.address2] - Override for address2 (e.g. condo unit number).
 */
function addressFields(label, { address2 } = {}) {
  const isValidState = (val) => !val || states.some((state) => state.id === val);
  const requiredWhenStarted = (message) =>
    function test(value) {
      if (isAddressEmpty(this.parent)) {
        return true; // whole address empty — the block/container summary owns this case
      }
      return !!value && !!String(value).trim() ? true : this.createError({ message, path: this.path });
    };
  return {
    // Terse, label-free subfield messages: the modal renders these under the address's section
    // header, so prefixing each with "<label> " (e.g. "Lienholder address street is required")
    // just echoes the header above. "Street is required" reads cleanly in that context.
    address: Yup.string().test('address-required', requiredWhenStarted('Street is required')),
    address2: address2 || Yup.string().nullable(),
    city: Yup.string().test('city-required', requiredWhenStarted('City is required')),
    state: Yup.string()
      .test('state-required', requiredWhenStarted('State is required'))
      .test(`${label}.validUSState`, 'Please enter a valid US state', isValidState),
    zip: Yup.string()
      .test('zip-required', requiredWhenStarted('Zip is required'))
      .test('zip-length', 'Zip must be 5 digits', (val) => !val || String(val).length >= 5)
  };
}

/**
 * A required NESTED address object (subfields under a child `address` key) with collapsed
 * messaging. A fully-empty block surfaces one "<label> is required" via the object-level
 * test; subfield rules take over once it's in-progress. See {@link addressFields}.
 */
export function addressBlock(label, opts = {}) {
  return Yup.object()
    .shape(addressFields(label, opts))
    .test('address-block-required', `${label} is required`, function test(value) {
      if (isAddressEmpty(value)) {
        return this.createError({ message: `${label} is required`, path: this.path });
      }
      return true;
    });
}

// @precondition, needs to have affinityLookups store object in context
export function validAffinityCode(name, ctx) {
  const { affinityLookups, isAgency } = ctx;
  return this.test({
    name: `${name}.validAffinityCode`,
    message: 'Please enter a valid affinity partner name or code',
    test: function test(val) {
      if (val && val !== 'None') {
        const matchedToAffinityInLookups = affinityLookups.data.find(({ affinity }) => affinity === val);

        if (!matchedToAffinityInLookups) {
          return this.createError({
            message: `Affinity value ${val} not found in our database. ${
              isAgency ? 'Please contact your designated agency success manager to fix the error to continue' : ''
            }`,
            path: name
          });
        }
      }
      return true;
    }
  });
}

export function validMortgageDetails(fieldName) {
  // The test passes if the payment method is not mortgage or if there's a value filled for mortgage holder name..
  // ...but we only need to check this in cases where the selected policy has a Home
  const normalizedFieldName = mortgageFieldsNameMap[fieldName];
  return this.test(
    'Require mortgage fields when home is added to policy',
    `${normalizedFieldName} is required`,
    function test(val) {
      const policyHasHome = this.options.context?.selectedOption?.includes('H') && !this.options.context.noBindHome;
      const policyHasCondo = this.options.context?.selectedOption?.includes('C');
      const policyHasFarm = isFarmBasedPolicyType(this.options.context?.selectedOption);

      const isHomeMortgaged = this.options.context?.global?.homeownersPaymentMethod === paymentMethod.Escrow;
      const isCondoMortgaged = this.options.context?.global?.condoPaymentMethod === paymentMethod.Escrow;
      const isFarmMortgaged = this.options.context?.global?.farmPaymentMethod === paymentMethod.Escrow;

      if (
        (policyHasHome && isHomeMortgaged) ||
        (policyHasCondo && isCondoMortgaged) ||
        (policyHasFarm && isFarmMortgaged)
      ) {
        return val && val?.trim().length > 0;
      }

      return true;
    }
  );
}

const commonBogusLoanNumbers = [
  'TBD',
  'NA',
  'N/A',
  'NONE',
  'NO',
  // no more than 6 repeating digits at any part of the loan number
  '000000',
  '111111',
  '222222',
  '333333',
  '444444',
  '555555',
  '666666',
  '777777',
  '888888',
  '999999'
];
function containsNumbers(str) {
  return /\d/.test(str);
}

function containsBogusInfo(str) {
  return new RegExp(commonBogusLoanNumbers.join('|'), 'i').test(str);
}

export function validMortgageLoanNumber(overridesForFeFormValidation) {
  return this.test('validLoanNumber', 'Please enter a valid loan number', function test(val) {
    const policyHasHome = this.options.context?.selectedOption?.includes('H');
    const policyHasCondo = this.options.context?.selectedOption?.includes('C');

    const isHomeMortgaged = this.options.context?.homeownersPaymentMethod === paymentMethod.Escrow;
    const isCondoMortgaged = this.options.context?.condoPaymentMethod === paymentMethod.Escrow;

    if ((policyHasHome && isHomeMortgaged) || (policyHasCondo && isCondoMortgaged)) {
      const hasRequiredDigits = containsNumbers(val) && val.length >= 4;
      const hasBogusPatterns =
        !overridesForFeFormValidation?.skipBogusLoanNumberCheck && (containsBogusInfo(val) || val?.startsWith('123'));

      if (!hasRequiredDigits || hasBogusPatterns) {
        return false; // user is probably trying to bypass the loan number by entering bad information
      }
      return true;
    }

    return true;
  });
}

export function validAffinityGroups() {
  return this.test('validAffinityGroups', '', (value, context) => {
    // If write affinity code was provided
    if (context.parent.affinity) {
      // Make sure affinity groups are provided and they include the write affinity code
      if (!value) {
        return context.createError({ message: 'Affinity groups are required when affinity code is set' });
      }

      if (!value.includes(context.parent.affinity)) {
        return context.createError({ message: 'Affinity groups must include the write affinity code' });
      }
    }

    return true;
  });
}

export const noPrimaryAddressTest = ({ primaryAddress, propertyType }) =>
  function (val) {
    if (val) {
      const context = this.options.context;
      const propertyAddress = context[propertyType];

      // Compare all address components
      const isSameAddress =
        propertyAddress?.address?.toUpperCase() === primaryAddress.address?.toUpperCase() &&
        propertyAddress?.state?.toUpperCase() === primaryAddress.state?.toUpperCase() &&
        propertyAddress?.city?.toUpperCase() === primaryAddress.city?.toUpperCase() &&
        propertyAddress?.zip === primaryAddress.zip;

      return !isSameAddress;
    }
    return true;
  };

export const requiredIfStreetAddress = function (message, path = 'farmLocations[0].legalDescription.format') {
  return this.test('required-if-street-address', message, function (value) {
    const legalDescriptionFormat = getIn(this.options.context, path);
    if (legalDescriptionFormat === 'street') {
      return !!value;
    }
    return true;
  });
};

export const requiredIfSectionTownshipRange = function (message, path = 'farmLocations[0].legalDescription.format') {
  return this.test('required-if-section-township-range', message, function (value) {
    const legalDescriptionFormat = getIn(this.options.context, path);
    if (legalDescriptionFormat === 'section-township-range') {
      return !!value;
    }
    return true;
  });
};

export const requiredIfLotBlock = function (message, path = 'farmLocations[0].legalDescription.format') {
  return this.test('required-if-lot-block', message, function (value) {
    const legalDescriptionFormat = getIn(this.options.context, path);
    if (legalDescriptionFormat === 'lot-block') {
      return !!value;
    }
    return true;
  });
};

export const isCountyRequired = function (message, path = 'farmLocations[0].legalDescription.format') {
  return this.test('required-if-section-township-range-or-lot-block', message, function (value) {
    const legalDescriptionFormat = getIn(this.options.context, path);
    if (['section-township-range', 'lot-block', 'long-legal'].includes(legalDescriptionFormat)) {
      return !!value;
    }
    return true;
  });
};

export const requiredIfAreasUnderConstruction = function (message) {
  return this.test('required-if-areas-under-construction', message, function (value) {
    // The path structure is: farmLocations[0].risks[0].building.renovation.fieldName
    // We need to check: farmLocations[0].risks[0].building.areasUnderConstruction
    const pathParts = this.path.split('.');
    // Remove the last two parts (renovation.fieldName) and add areasUnderConstruction
    const buildingPath = pathParts.slice(0, -2).join('.');
    const areasUnderConstruction = getIn(this.options.context, `${buildingPath}.areasUnderConstruction`);

    if (areasUnderConstruction) {
      return !!value;
    }
    return true;
  });
};

export const requiredIfFO4 = function (message, path = 'farmLocations[0].risks[0].type') {
  return this.test('required-when-fo4', message, function (value) {
    const farmRiskType = getIn(this.options.context, path);
    if (farmRiskType === FarmRiskType.Fo4) {
      return !!value;
    }
    return true;
  });
};

function validateValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== undefined && value !== null && value !== '';
}

/**
 * Yup validation helper that makes a field required based on risk type and property type.
 *
 * @param {Object} options
 * @param {string} options.message - Error message when validation fails
 * @param {Array<Object>} options.risks - Array of risk configurations
 * @param {string} options.risks[].risk - Risk type (e.g., FarmRiskType.Fo3, FarmRiskType.Fo6Structures)
 * @param {Array<string>} [options.risks[].requiredForPropertyTypes] - Optional array of property types to limit validation to.
 *   When specified and the property type matches, the field is ALWAYS required (overrides requireIfRatedOnly).
 * @param {Array<string>} [options.risks[].structureCategories] - Optional array of structure categories (buildingType values)
 *   to limit validation to. Used primarily for FO6 structures. When specified, field is only required if buildingType matches.
 * @param {boolean} [options.risks[].requireIfRatedOnly] - If true, only validate when risk has been rated (has an id).
 *   Note: This is ignored if requiredForPropertyTypes is specified and matches the current property type.
 * @param {Array<string>|Function} [options.risks[].excludedPropertyTypes] - Property types to skip validation for.
 *   Can be a static string[] or a function ({ state }) => string[] for brand-config-driven lookups.
 *
 * @example
 * // Required for FO3 risks only
 * fieldName: yup.string().requiredForRisks({
 *   message: 'Field is required',
 *   risks: [{ risk: FarmRiskType.Fo3 }]
 * })
 *
 * @example
 * // Required for FO6 structures, but only for mobile homes (MH) property type
 * fieldName: yup.string().requiredForRisks({
 *   message: 'Field is required for mobile homes',
 *   risks: [{ risk: FarmRiskType.Fo6Structures, requiredForPropertyTypes: ['MH'] }]
 * })
 *
 * @example
 * // Required for multiple risk types, only after risk has been rated
 * fieldName: yup.string().requiredForRisks({
 *   message: 'Field is required',
 *   risks: [
 *     { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
 *     { risk: FarmRiskType.Fo4, requireIfRatedOnly: true }
 *   ]
 * })
 *
 * @example
 * // Required for FO3 with property type 'B' always, but other property types only when rated
 * fieldName: yup.string().requiredForRisks({
 *   message: 'Field is required',
 *   risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'], requireIfRatedOnly: true }]
 * })
 *
 * @example
 * // Required for FO6 structures, but only for specific building types (structure categories)
 * fieldName: yup.string().requiredForRisks({
 *   message: 'Field is required',
 *   risks: [{
 *     risk: FarmRiskType.Fo6Structures,
 *     structureCategories: [StructureCategory.BarnsOutbuildings, StructureCategory.ConfinementBuildings]
 *   }]
 * })
 */
// Valid farm risk type codes (FO1, FO2, FO3, FO4, FO6Structures, …).
const FARM_RISK_TYPES = new Set(Object.values(FarmRiskType));

// A farm risk node carries a valid FarmRiskType `type` plus either a `building` or a
// `coverages` object. Validating `type` against the enum (rather than just "defined")
// guards against accidentally matching a non-risk ancestor that happens to expose a
// `type` field. Used to locate the risk in Yup's ancestor chain.
const isRiskNode = (node) =>
  !!node && FARM_RISK_TYPES.has(node.type) && (node.building !== undefined || node.coverages !== undefined);

// Resolve the risk being validated from Yup's `this.from` ancestor list
// (innermost → outermost). This is form-nesting agnostic, so it works whether
// the farm form is mounted at the values root (new offer) or nested under a
// segment draft (policy-mod / endorsement). Mirrors how the coverage validators
// locate the risk via `this.from`.
const findRiskInAncestors = (from) =>
  (Array.isArray(from) ? from.find((entry) => isRiskNode(entry?.value)) : undefined)?.value;

// Location node is the ancestor that owns a `risks` array; used for address.state.
export const findLocationStateInAncestors = (from) =>
  (Array.isArray(from) ? from.find((entry) => Array.isArray(entry?.value?.risks)) : undefined)?.value?.address?.state;

export const requiredForRisks = function ({ message, risks = [], relaxOnFarmMigration = false }) {
  return this.test('required-for-risks', message, function (value) {
    if (risks.length === 0) {
      return true;
    }

    // #33509: on a migrated-and-renewed farm term an underwriter may proceed without this field so a
    // gap the converter couldn't fill (e.g. roof shape) doesn't block an unrelated edit. Opt-in per
    // field via `relaxOnFarmMigration`; the context flag is set in details.jsx from
    // shouldRelaxFarmUWRequirements (UW role AND wasRenewedFromImportedTerm).
    if (relaxOnFarmMigration && this.options.context?.relaxFarmMigrationRequirements) {
      return true;
    }

    // Resolve the risk relative to the field (this.from). Falls back to the
    // absolute context-path lookup for any caller whose ancestor chain doesn't
    // include the risk. Using this.from is what makes the rule fire identically
    // on a new offer and inside a policy-mod endorsement draft, where the farm
    // form is nested and the absolute lookup misses the risk (so its backend
    // `id` was never seen and `requireIfRatedOnly` wrongly skipped). #31480.
    let risk = findRiskInAncestors(this.from);
    if (!risk) {
      const [locationPath, riskPath] = (this.path || '').split('.');
      risk = getIn(this.options.context, `${locationPath}.${riskPath}`);
    }
    if (!risk) {
      return true;
    }

    const {
      id,
      type: riskType,
      building: { propertyType, buildingType } = {}
    } = risk;

    const riskHasBeenRated = !!id; // Risk will have backend generated id if it has been rated
    const matchingRiskConfig = risks.find((r) => r.risk === riskType);

    if (!matchingRiskConfig) {
      return true; // Risk type not in required list
    }

    const { requiredForPropertyTypes, structureCategories, requireIfRatedOnly, excludedPropertyTypes } =
      matchingRiskConfig;

    // If property type is excluded, skip validation
    // excludedPropertyTypes can be a static string[] or a function ({ state }) => string[]
    if (excludedPropertyTypes) {
      const state =
        findLocationStateInAncestors(this.from) ??
        getIn(this.options.context, `${(this.path || '').split('.')[0]}.address.state`);
      const resolvedExcludedTypes =
        typeof excludedPropertyTypes === 'function' ? excludedPropertyTypes({ state }) : excludedPropertyTypes;
      if (resolvedExcludedTypes && resolvedExcludedTypes.includes(propertyType)) {
        return true;
      }
    }

    // If structureCategories is specified, check if buildingType matches
    // This is used for FO6 structures where different building types have different requirements
    if (structureCategories && structureCategories.length > 0) {
      if (!structureCategories.includes(buildingType)) {
        return true; // Building type not in required list, skip validation
      }
    }

    // If requiredForPropertyTypes is specified, check if property type matches
    if (requiredForPropertyTypes && requiredForPropertyTypes.length > 0) {
      if (requiredForPropertyTypes.includes(propertyType)) {
        // Property type matches - always require (overrides requireIfRatedOnly)
        return validateValue(value);
      }

      return true;
    }

    // Check requireIfRatedOnly (applies when no property type filter OR property type didn't match)
    if (requireIfRatedOnly && !riskHasBeenRated) {
      return true;
    }

    return validateValue(value);
  });
};

export const requiredIfFO6BarnsOutbuildings = function (message) {
  return this.test('required-for-fo6-barns-outbuildings', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');

    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'BO') {
      return value != null;
    }
    return true;
  });
};

export const requiredIfFO6BarnsOutbuildingsAndHeatingEnabled = function (message) {
  return this.test('required-for-fo6-barns-outbuildings-when-enabled', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');
    const primaryHeatingSource = getIn(this.parent, 'primaryHeatingSource');

    // Only require if it's FO6 Barns/Outbuildings and the heating source is unit count based
    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'BO' && primaryHeatingSource) {
      // Check if heating source is unit count based using lookup data
      const heatingOption = primaryHeatingSourceOptions.find((option) => option.id === primaryHeatingSource);
      if (heatingOption?.unitCountBased) {
        return !!value;
      }
    }
    return true;
  });
};

export const requiredForTowers = function (message) {
  return this.test('required-for-towers', message, function (value) {
    const buildingType = getIn(this.parent, 'buildingType');
    const occupancy = getIn(this.parent, 'occupancy');

    if (buildingType === 'SG' && (occupancy === 'GHT' || occupancy === 'GHH')) {
      return !!value;
    }
    return true;
  });
};

export const requiredForGrainStorage = function (message) {
  return this.test('required-for-grain-storage', message, function (value) {
    const occupancy = getIn(this.parent, 'occupancy');

    if (occupancy === 'GHB') {
      return !!value;
    }
    return true;
  });
};

export const requiredForNonTowerSilos = function (message) {
  return this.test('required-for-non-tower-silos', message, function (value) {
    const buildingType = getIn(this.parent, 'buildingType');
    const occupancy = getIn(this.parent, 'occupancy');

    if (
      buildingType === 'SG' &&
      occupancy &&
      occupancy !== 'GHT' &&
      occupancy !== 'GHB' &&
      occupancy !== 'GHH' &&
      occupancy !== 'GL'
    ) {
      return !!value;
    }
    return true;
  });
};

export const requiredIfFO6SilosGrainBins = function (message) {
  return this.test('required-for-fo6-silos-grain-bins', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');

    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'SG') {
      return !!value;
    }
    return true;
  });
};

export const requiredIfFO6SilosGrainBinsNonTower = function (message) {
  return this.test('required-for-fo6-silos-grain-bins-non-tower', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');
    const occupancy = getIn(this.parent, 'occupancy');

    if (
      riskType === FarmRiskType.Fo6Structures &&
      buildingType === 'SG' &&
      occupancy !== 'GHT' &&
      occupancy !== 'GHB' &&
      occupancy !== 'GHH' &&
      occupancy !== 'GL'
    ) {
      return !!value;
    }
    return true;
  });
};

export const requiredIfFO6Poultry = function (message) {
  return this.test('required-for-fo6-poultry', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');

    if (riskType === FarmRiskType.Fo6Structures && ['PB', 'PI', 'PO'].includes(buildingType)) {
      return value != null;
    }
    return true;
  });
};

export const requiredIfFO6PoultryAndHeatingEnabled = function (message) {
  return this.test('required-for-fo6-poultry-when-enabled', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');
    const primaryHeatingSource = getIn(this.parent, 'primaryHeatingSource');

    // Only require if it's FO6 Poultry and the heating source is unit count based
    if (riskType === FarmRiskType.Fo6Structures && ['PB', 'PI', 'PO'].includes(buildingType) && primaryHeatingSource) {
      // Check if heating source is unit count based using lookup data
      const heatingOption = primaryHeatingSourceOptions.find((option) => option.id === primaryHeatingSource);
      if (heatingOption?.unitCountBased) {
        return !!value;
      }
    }
    return true;
  });
};

export const requiredIfFO6SilosGrainBinsAndHeatingEnabled = function (message) {
  return this.test('required-for-fo6-silos-grain-bins-when-enabled', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');
    const primaryHeatingSource = getIn(this.parent, 'primaryHeatingSource');

    // Only require if it's FO6 Silos/Grain Bins and the heating source is unit count based
    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'SG' && primaryHeatingSource) {
      // Check if heating source is unit count based using lookup data
      const heatingOption = primaryHeatingSourceOptions.find((option) => option.id === primaryHeatingSource);
      if (heatingOption?.unitCountBased) {
        return !!value;
      }
    }
    return true;
  });
};

export const requiredIfFO6ConfinementBuildings = function (message) {
  return this.test('required-for-fo6-confinement-buildings', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');

    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'C') {
      return value != null;
    }
    return true;
  });
};

export const requiredIfFO6HoopBuildings = function (message) {
  return this.test('required-for-fo6-hoop-buildings', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');

    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'H') {
      return !!value;
    }
    return true;
  });
};

export const requiredIfFO6DwellingContents = function (message) {
  return this.test('required-for-fo6-dwelling-contents', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    // For fields inside `building`, this.parent is the building object and buildingType is a sibling.
    // For fields inside `coverages` (e.g. coverageE), this.parent is coverages, so we fall back to
    // this.from[1].value.building.buildingType to reach the sibling building object.
    const buildingType = getIn(this.parent, 'buildingType') ?? getIn(this.from?.[1]?.value, 'building.buildingType');

    // GitHub issue #29255: Always require for Dwelling and Contents regardless of canEditCoverageAmount toggle.
    // For numeric fields (e.g. coverageE), require value > 0; for string fields (e.g. residenceType), require non-empty.
    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'DC') {
      return typeof value === 'number' ? value > 0 : !!value;
    }
    return true;
  });
};

export const requiredIfFO6Fencing = function (message) {
  return this.test('required-for-fo6-fencing', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');

    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'F') {
      return value != null && value > 0;
    }
    return true;
  });
};

export const requiredIfFO6DwellingContentsAndHeatingEnabled = function (message) {
  return this.test('required-for-fo6-dwelling-contents-when-heating-enabled', message, function (value, { options }) {
    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');
    const primaryHeatingSource = getIn(this.parent, 'primaryHeatingSource');

    // Only require if it's FO6 Dwelling/Contents and the heating source is unit count based
    if (riskType === FarmRiskType.Fo6Structures && buildingType === 'DC' && primaryHeatingSource) {
      // Check if heating source is unit count based using lookup data
      const heatingOption = primaryHeatingSourceOptions.find((option) => option.id === primaryHeatingSource);
      if (heatingOption?.unitCountBased) {
        return !!value;
      }
    }
    return true;
  });
};

export const requiredWhenCanEditCoverageAmount = function (message) {
  return this.test('required-when-can-edit-coverage-amount', message, function (value) {
    if (!getIn(this.parent, 'canEditCoverageAmount')) {
      return true;
    }
    return value !== undefined && value !== null;
  });
};

// #34201: when an underwriter refreshes the 360Value on a migrated (imported-then-renewed) term via
// `uwRefresh360Value`, they may override dwelling Coverage A down to this $1,000 floor with no upper
// cap. Mirrors UW_REFRESH_COVERAGE_A_FLOOR in the cfm/forreston farm-post-data-rules so FE and BE agree.
const UW_REFRESH_COVERAGE_A_FLOOR = 1000;

export const coverageInRangeRequiredWhenEditable = function (requiredForRiskTypes = []) {
  return this.test('coverage-in-range-required-when-editable', 'Coverage must be in range', function (value) {
    // Issue #28846: Coverage A is now always editable, always validate
    const pathParts = this.options.path.split('.');
    const [locationPath, riskPath] = pathParts;
    const fieldName = pathParts[pathParts.length - 1]; // e.g., 'coverageA' or 'coverageE'
    const coverageLabel = fieldName.replace(/^coverage([A-Z])$/, 'Coverage $1') || fieldName;
    const riskType = getIn(this.options.context, `${locationPath}.${riskPath}.type`);
    if (requiredForRiskTypes.length > 0 && !requiredForRiskTypes.includes(riskType)) {
      return true;
    }

    // Check if "Adjust dwelling limit more" toggle is enabled
    const canAdjustMore = getIn(this.parent, 'canEditCoverageAmount');

    // #34201: once an underwriter has approved a sub-floor Coverage A (coverageAFloorApprovedByUW),
    // it may sit down to a $1,000 floor with no upper cap. Skip the RCE/ACV/brand min & max checks
    // below and enforce only the hard floor. Coverage E on FO-6 keeps its own rules (per #34201, "no
    // rule changes"), so this only relaxes Coverage A. This is the PERSISTED marker (not the one-shot
    // uwRefresh360Value), so it survives re-rates/agent binds and never re-blocks a UW-lowered value.
    if (fieldName === 'coverageA' && getIn(this.parent, 'coverageAFloorApprovedByUW')) {
      if (value != null && value < UW_REFRESH_COVERAGE_A_FLOOR) {
        return this.createError({
          message: `Coverage A cannot be below $${UW_REFRESH_COVERAGE_A_FLOOR.toLocaleString()}`
        });
      }
      return true;
    }

    // Special handling for Coverage E on FO-6 Structures (Issue #28960)
    if (fieldName === 'coverageE' && riskType === FarmRiskType.Fo6Structures) {
      const building = this.from && this.from.length >= 2 ? getIn(this.from[1].value, 'building') : null;
      const buildingClass = building?.buildingClass;
      // $7,500 minimum if buildingClass === 1; otherwise $1,000 minimum
      const minimumCoverage = buildingClass === 1 ? 7500 : 1000;

      // null value means the field hasn't been filled in yet; let requiredIfFO6Fencing handle that
      if (value != null && value < minimumCoverage) {
        return this.createError({
          message: `Coverage E cannot be below $${minimumCoverage.toLocaleString()}`
        });
      }
    }
    // Minimum Coverage A floor - use brand-config-driven minimum (Issue #28995)
    // Forreston uses $1,000; default for other brands is $75,000
    // Applies to all risk types except FO1/FO2 which have ACV-based minimums
    else if (riskType !== FarmRiskType.Fo1 && riskType !== FarmRiskType.Fo2) {
      const state = getIn(this.options.context, `${locationPath}.address.state`);
      const minimumDwellingCoverageA = appConfig?.farm?.[state]?.coverageLimits?.minimumDwellingCoverageA ?? 75000;
      if (value < minimumDwellingCoverageA) {
        return this.createError({
          message: `${riskType === FarmRiskType.Fo3 ? 'Dwelling limit' : 'Scheduled property limit'} cannot be below $${minimumDwellingCoverageA.toLocaleString()}`
        });
      }
    }

    // Issue #28616: Use brand config business rules for ACV-based validation
    // Issue #28846: When toggle is ON, skip max validation; when OFF, enforce both min and max
    // Check if this brand has ACV-based business rules for this risk type
    const state = getIn(this.options.context, `${locationPath}.address.state`);
    const lossSettlement =
      this.from && this.from.length >= 2 ? getIn(this.from[1].value, 'coverages.lossSettlement') : null;
    const businessRules = appConfig?.farm?.[state]?.risks?.[riskType]?.businessRules?.[fieldName];

    // Only apply ACV-based validation if:
    // 1. Brand has defined business rules for this risk type and state
    // 2. ACV loss settlement is selected (FO1 always ACV, FO2 can be ACV or RC)
    if (businessRules && lossSettlement === 'ACV') {
      const building = this.from && this.from.length >= 2 ? getIn(this.from[1].value, 'building') : null;
      const acv = building?.actualCashValueCalculation?.total;
      const rce = building?.replacementCostEstimate?.total;
      const propertyType = building?.propertyType;
      const sqFt = building?.sqFt;

      if (!acv) {
        // Skip ACV validation if ACV is not available (should be caught by backend 50006 error)
        return true;
      }

      // Mobile Home: minimum per sq ft, max of ACV or RCE
      if (businessRules.mobileHome?.propertyTypes?.includes(propertyType)) {
        if (!sqFt) {
          return this.createError({
            message: 'Square footage is required for Mobile Home validation'
          });
        }

        const minValue = sqFt * businessRules.mobileHome.minPerSqFt;
        const maxValue = Math.max(acv, rce || 0);

        if (value < minValue) {
          return this.createError({
            message: `${coverageLabel} must be at least $${minValue.toLocaleString()} (based on ${sqFt} sq ft at $${businessRules.mobileHome.minPerSqFt}/sq ft)`
          });
        }

        // Only enforce max if toggle is OFF
        if (!canAdjustMore && value > maxValue) {
          return this.createError({
            message: `${coverageLabel} must not exceed $${maxValue.toLocaleString()} (greater of ACV or RCE) unless "Adjust dwelling limit more" is enabled`
          });
        }
      }
      // Home/Modular/Barndominium: percentage of ACV, max of ACV or RCE
      else if (businessRules.acvBasedStructures?.propertyTypes?.includes(propertyType)) {
        const minValue = Math.round(acv * businessRules.acvBasedStructures.minPercent);
        const maxValue = Math.max(acv, rce || 0);

        if (value < minValue) {
          return this.createError({
            message: `${coverageLabel} must be at least $${minValue.toLocaleString()} (${businessRules.acvBasedStructures.minPercent * 100}% ACV)`
          });
        }

        // Only enforce max if toggle is OFF
        if (!canAdjustMore && value > maxValue) {
          return this.createError({
            message: `${coverageLabel} must not exceed $${maxValue.toLocaleString()} (greater of ACV or RCE) unless "Adjust dwelling limit more" is enabled`
          });
        }
      }

      return true;
    }

    // RCE-based range validation only applies to Coverage A (dwelling limit)
    if (fieldName !== 'coverageA') {
      return true;
    }

    // Get original RCE value and yearBuilt from form state, falling back to initial values if schema context unavailable
    let originalRCEValue;
    let yearBuilt;

    if (this.from && this.from.length >= 2) {
      const building = getIn(this.from[1].value, 'building');
      originalRCEValue = building?.replacementCostEstimate?.total;
      yearBuilt = building?.yearBuilt;
    }

    // If RCE data is not available from form state, try to get original value from initialValues
    if (!originalRCEValue || originalRCEValue === 0) {
      originalRCEValue = getIn(this.options.context, `initialValues.${locationPath}.${riskPath}.coverages.coverageA`);
    }

    // Skip RCE range validation if RCE data is unavailable (new risks, failed data pulls)
    if (!originalRCEValue || originalRCEValue === 0) {
      return true; // Only validate $75k minimum (lines 604-608)
    }

    // #33567: Expanded Replacement Cost requires the FO3 dwelling to be insured to 100% of RCE, so
    // its Coverage A floor rises from the usual 90% to 100%. (Functional-RC dwellings — built 1950
    // or earlier — have replacementCostExp cleared, so this only affects genuine Expanded RC opt-ins.)
    const isExpandedReplacementCost = riskType === FarmRiskType.Fo3 && !!getIn(this.parent, 'replacementCostExp');

    // Use brand-config rceBasedStructures if available (Issue #29183)
    // This enables year-built-aware minimums (e.g., 50% for FO2 RC pre-1950)
    // Only applies for RC loss settlement; ACV is handled by the block above
    if (businessRules?.rceBasedStructures && lossSettlement === 'RC') {
      const rceRules = businessRules.rceBasedStructures;
      let minPercent = rceRules.minPercent;

      // Year-built override (e.g., 50% min for homes built 1950 or before)
      // If yearBuilt is null/undefined (unknown build year), no override is applied and
      // the default minPercent (e.g. 80%) is enforced — the conservative safe default.
      if (rceRules.yearBuiltOverrides) {
        if (yearBuilt != null && yearBuilt <= rceRules.yearBuiltOverrides.threshold) {
          minPercent = rceRules.yearBuiltOverrides.minPercentAtOrBefore;
        }
      }

      // #33567: never below 100% RCE when Expanded RC is active (see note above).
      if (isExpandedReplacementCost) {
        minPercent = Math.max(minPercent, 1);
      }

      const minRCE = originalRCEValue * minPercent;
      const maxRCE = originalRCEValue * rceRules.maxPercent;

      if (value < minRCE) {
        return this.createError({
          message: `Coverage A must be at least $${Math.round(minRCE).toLocaleString()} (${Math.round(minPercent * 100)}% RCE)`
        });
      }

      if (!canAdjustMore && value > maxRCE) {
        return this.createError({
          message: `${coverageLabel} must not exceed $${Math.round(maxRCE).toLocaleString()} (${Math.round(rceRules.maxPercent * 100)}% RCE) unless "Adjust dwelling limit more" is enabled`
        });
      }

      return true;
    }

    // Hardcoded fallback for brands without rceBasedStructures config
    const isFo3 = riskType === FarmRiskType.Fo3;
    // #33567: Expanded RC forces a 100% floor; otherwise FO3 is 90% and everything else 80%.
    const fallbackMinPercent = isExpandedReplacementCost ? 1 : isFo3 ? 0.9 : 0.8;
    const minRCE = originalRCEValue * fallbackMinPercent;
    const maxRCE = originalRCEValue;

    if (value < minRCE) {
      return this.createError({
        message: `Coverage A must be at least $${Math.round(minRCE).toLocaleString()} (${Math.round(fallbackMinPercent * 100)}% RCE)`
      });
    }

    // Only enforce max if toggle is OFF
    if (!canAdjustMore && value > maxRCE) {
      return this.createError({
        message: `${coverageLabel} must not exceed $${Math.round(maxRCE).toLocaleString()} (100% RCE) unless "Adjust dwelling limit more" is enabled`
      });
    }

    return true;
  });
};

// #31836: minimum Coverage E an underwriter may set on FO6 ACV items
// (Barns Type 3, Fencing, Hoop, Silos/Grain Bins), overriding the 80% ACV floor.
const UW_ACV_COVERAGE_E_FLOOR = 1000;

/**
 * Validates that coverage meets 80% of Replacement Cost Estimate (RCE) when RC loss settlement is selected
 * @param {Object} options - Configuration options
 * @param {Array<string>} options.validRiskTypes - Risk types that require this validation (e.g., ['FO3'])
 * @returns {Yup.test} Yup validation test
 */
export const rc80PercentRequirement = function (options = {}) {
  return this.test('rc-80-percent-requirement', function (value) {
    // #33509: a UW servicing a migrated-and-renewed term should not be blocked by the Coverage E
    // *floors*. Migrated structures came over with their legacy Coverage E and no Verisk RCE/ACV, so
    // the RC/ACV 80% minimums — and the "RC selected but RCE data not available" error — would block an
    // unrelated edit. The BE exempts these terms from those floors (farm-post-data-rules
    // skipCoverageFloorsForMigratedTerm) but STILL enforces the maximum caps, so scope this to the
    // minimums only (guarded at each floor/availability check below) rather than skipping the whole rule.
    const relaxMigrationFloor = Boolean(this.options.context?.relaxFarmMigrationRequirements);
    // If specific risk types are specified, only validate for those types
    if (options.validRiskTypes && options.validRiskTypes.length > 0) {
      const risk = getIn(this.from[1].value, '');
      if (!options.validRiskTypes.includes(risk?.type)) {
        return true;
      }
    }

    // Get form context
    const structureLossSettlement = getIn(this.parent, 'structureLossSettlement');
    const roofLossSettlement = getIn(this.parent, 'roofLossSettlement');

    // ACV-based validation: 80% of ACV minimum, capped at RCE (or ACV if no RCE)
    // Related issues: #28950, #28923, #29153
    if (structureLossSettlement === 'ACV') {
      const building = getIn(this.from[1].value, 'building');
      const isNewRisk = !getIn(this.from[1].value, 'id');
      const acvTotal = building?.actualCashValueCalculation?.total;

      if (isNewRisk && (!acvTotal || acvTotal === 0)) {
        return true;
      }

      if (acvTotal > 0) {
        const coverageValue = value || 0;
        const eightyPercentAcv = Math.round(acvTotal * 0.8);
        // #31836: agents are held to the 80% ACV floor, but underwriters may lower
        // FO6 ACV Coverage E items down to a $1,000 minimum. Use the lower of the two
        // so the UW floor is never *more* restrictive than the agent floor on tiny ACVs.
        // Once a UW has approved a sub-floor value (acvFloorApprovedByUW persisted on the
        // coverages), the $1,000 floor applies to EVERY viewer — otherwise an agent binding a
        // UW-lowered offer would be re-blocked here by the 80% floor (the reopened #31836 bug).
        const isUnderwriter = Boolean(this.options.context?.isUnderwriter);
        const uwApprovedAcvFloor = Boolean(getIn(this.parent, 'acvFloorApprovedByUW'));
        const minimumRequired =
          isUnderwriter || uwApprovedAcvFloor ? Math.min(UW_ACV_COVERAGE_E_FLOOR, eightyPercentAcv) : eightyPercentAcv;
        if (!relaxMigrationFloor && coverageValue < minimumRequired) {
          return this.createError({
            message: isUnderwriter
              ? `Coverage E must be at least $${minimumRequired.toLocaleString()}.`
              : `Coverage E must be at least 80% of ACV ($${minimumRequired.toLocaleString()}). Current ACV: $${acvTotal.toLocaleString()}.`
          });
        }

        // Maximum cap: RCE if available, otherwise ACV
        // Skip cap when "Adjust scheduled property limit" toggle is ON
        const canAdjustMore = getIn(this.parent, 'canEditCoverageAmount');
        const rce = building?.replacementCostEstimate?.total;
        const hasRce = rce && rce > 0;
        const maxCoverage = hasRce ? rce : acvTotal;
        // Only enforce cap if it doesn't contradict the minimum requirement
        if (!canAdjustMore && maxCoverage >= minimumRequired && coverageValue > maxCoverage) {
          const capLabel = hasRce ? '100% RCE' : '100% ACV';
          return this.createError({
            message: `Coverage E must not exceed $${Math.round(maxCoverage).toLocaleString()} (${capLabel}). Current ${hasRce ? 'RCE' : 'ACV'}: $${Math.round(maxCoverage).toLocaleString()}.`
          });
        }
      }
      return true;
    }

    if (structureLossSettlement === 'RC' || roofLossSettlement === 'RC') {
      const building = getIn(this.from[1].value, 'building');
      const isNewRisk = !getIn(this.from[1].value, 'id'); // Use hasBeenRated pattern
      const rce = building?.replacementCostEstimate?.total;

      // Allow new risks to use RC without RCE validation
      if (isNewRisk && (!rce || rce === 0)) {
        return true; // Pass validation for new risks
      }

      // Existing validation logic for rated risks
      // Related issue: #29153
      if (rce && value) {
        const minimumRequired = rce * 0.8;
        if (!relaxMigrationFloor && value < minimumRequired) {
          return this.createError({
            message: `Coverage E must be at least 80% of replacement cost ($${minimumRequired.toLocaleString()}) when RC is selected. Current RCE: $${rce.toLocaleString()}.`
          });
        }

        // Maximum cap: 100% of RCE, unless "Adjust scheduled property limit" toggle is ON
        const canAdjustMore = getIn(this.parent, 'canEditCoverageAmount');
        const maxCoverage = rce;
        if (!canAdjustMore && value > maxCoverage) {
          return this.createError({
            message: `Coverage E must not exceed $${Math.round(maxCoverage).toLocaleString()} (100% RCE). Current RCE: $${rce.toLocaleString()}.`
          });
        }
        return true;
      }

      if (!relaxMigrationFloor && !isNewRisk) {
        // Only error for existing risks missing RCE (relaxed for migrated terms — no Verisk RCE exists)
        return this.createError({
          message: 'RC loss settlement selected but RCE data not available for validation'
        });
      }
    }

    return true;
  });
};

export function validYearBuilt(message) {
  return this.test('valid-year-built', message || 'Please enter a valid year', function (value) {
    if (!value) {
      return true; // Let required validation handle empty values
    }

    const year = parseInt(value, 10);
    const currentYear = new Date().getFullYear();
    const minYear = 1800;
    const maxYear = currentYear + 2; // Allow for future construction dates

    // Check if it's a valid number
    if (isNaN(year)) {
      return this.createError({
        message: 'Year must be a valid number'
      });
    }

    // Check if year is in valid range
    if (year < minYear || year > maxYear) {
      return this.createError({
        message: `Year must be between ${minYear} and ${maxYear}`
      });
    }

    return true;
  });
}

export function mobileHomeSqFtValidation() {
  return this.test(
    'mobile-home-sqft-validation',
    'Square footage must match mobile home configuration',
    function (value) {
      const propertyType = getIn(this.parent, 'propertyType');
      const mobileHomeConfig = getIn(this.parent, 'mobileHomeConfig');

      // Only validate if it's a mobile home with a configuration selected
      if (propertyType === 'MH' && mobileHomeConfig && value) {
        switch (mobileHomeConfig) {
          case 'SW': // Single-wide
            if (value >= 1500) {
              return this.createError({ message: 'Single-wide mobile homes must have less than 1,500 sq ft' });
            }
            break;
          case 'DW': // Double-wide
            if (value < 1000 || value > 3000) {
              return this.createError({ message: 'Double-wide mobile homes must have between 1,000 and 3,000 sq ft' });
            }
            break;
          case 'TW': // Triple-wide
            if (value <= 2000) {
              return this.createError({ message: 'Triple-wide mobile homes must have more than 2,000 sq ft' });
            }
            break;
        }
      }
      return true;
    }
  );
}

/**
 * Helper function to determine if a risk is a dwelling or renters risk type
 * Used for swimming pool validations that only apply to FO1/FO2/FO3/FO4
 * @param {Object} options - Yup test options object
 * @returns {boolean} True if risk type is a dwelling or renters risk
 */
export const isDwellingOrRentersRisk = (options) => {
  // Path uses bracket notation: farmLocations[0].risks[0].building...
  const [locationPath, riskPath] = options.path.split('.');
  if (!locationPath || !riskPath) {
    return false;
  }

  const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
  return [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType);
};

/**
 * Helper function to determine if a risk requires primary heating validation
 * Only requires primary heating when the field is shown in the UI
 * Applies to: FO1, FO2, FO3, FO4 (all dwelling/renters risks)
 * For FO6: Only shown for specific building types (BO, C, H, DC - NOT SG or PO)
 * @param {Object} options - Yup test options object
 * @returns {boolean} True if primary heating field is shown and should be required
 */
export const requiresPrimaryHeating = (options) => {
  // Path uses bracket notation: farmLocations[0].risks[0].building...
  const [locationPath, riskPath] = options.path.split('.');
  if (!locationPath || !riskPath) {
    return false;
  }

  const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);

  // For dwelling and renters risks (FO1, FO2, FO3, FO4), primary heating is always shown
  if ([FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType)) {
    return true;
  }

  // For FO6 structures, only require if the field is shown (based on building type)
  if (riskType === FarmRiskType.Fo6Structures) {
    const buildingType = getIn(options.context, `${locationPath}.${riskPath}.building.buildingType`);

    // Primary heating is shown for these structure categories:
    // BO (Barns/Outbuildings), C (Confinement), H (Hoop), DC (Dwelling/Contents)
    // NOT shown for: SG (Silos/Grain Bins), PO (Poultry Buildings)
    const structureTypesWithHeating = ['BO', 'C', 'H', 'DC'];
    return structureTypesWithHeating.includes(buildingType);
  }

  return false;
};

/**
 * Helper function to determine if a risk requires swimming pool type validation
 * Applies to: FO1, FO2, FO3, FO4 only (NOT FO6)
 * @param {Object} options - Yup test options object
 * @returns {boolean} True if risk type requires swimming pool type
 */
export const requiresSwimmingPoolType = (options) => {
  // Path uses bracket notation: farmLocations[0].risks[0].building.swimmingPool.typeOfPool
  const [locationPath, riskPath] = options.path.split('.');
  if (!locationPath || !riskPath) {
    return false;
  }

  const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);

  return [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType);
};

export function nullifyIfNaN() {
  return this.transform((value) => {
    // Convert NaN values to null to avoid validation errors
    if (typeof value === 'number' && isNaN(value)) {
      return null;
    }
    // Convert string 'NaN' to null as well
    if (typeof value === 'string' && value === 'NaN') {
      return null;
    }
    return value;
  });
}

export const requiredForFO6Structures = function (message) {
  return this.test('required-fo6-structures', message, function (value) {
    const { options } = this;

    // Path uses bracket notation: farmLocations[0].risks[0].building...
    const [locationPath, riskPath] = options.path.split('.');
    if (!locationPath || !riskPath) {
      return true; // Not in a risk building context, skip validation
    }

    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const buildingType = getIn(this.parent, 'buildingType');

    // If it's FO6 Structures, year built is required for all building types.
    // Non-DC types are covered here; DC types are additionally covered by requiredIfFO6DwellingContents.
    if (riskType === FarmRiskType.Fo6Structures && buildingType !== 'DC') {
      return !!value;
    }

    return true; // Not FO6 Structures or handled by requiredIfFO6DwellingContents for DC
  });
};

/**
 * Validates that single-wide and double-wide mobile homes are not eligible for FO-3.
 * Triple-wide mobile homes remain eligible for FO-3.
 *
 * @param {string} message - Error message to display when validation fails
 * @returns {Yup.test} Yup validation test
 */
export const invalidMobileHomeConfigForFo3 = function (message) {
  return this.test('invalid-mobile-home-config-for-fo3', message, function (value, { options }) {
    // Only validate if value is set
    if (!value) {
      return true;
    }

    const [locationPath, riskPath] = options.path.split('.');
    const riskType = getIn(options.context, `${locationPath}.${riskPath}.type`);
    const propertyType = getIn(this.parent, 'propertyType');

    // Only fail validation for FO3 + Mobile Home + Single/Double wide
    if (riskType === FarmRiskType.Fo3 && propertyType === 'MH' && ['SW', 'DW'].includes(value)) {
      return false;
    }

    return true;
  });
};
